import Web3 from 'web3'
import BigNumber from 'bignumber.js'
import { Interface } from '@ethersproject/abi'
import { getChainFromId, buildWalletAddEthereumChainParams } from './config.mjs'
import { getProvider } from './inject.mjs'
import HieSwapAbi from './abis/HieSwap.js'
import ExchangeAbi from './abis/Exchange.js'
import Erc20Abi from './abis/IERC20.js'
import {base58de, base58en, triggerconstantcontract} from './tron'
import { db } from '../utils.mjs'
const WITHDRAW = '0x8971A707c958e1EaE4147FF0318117049f50c6d2'

export const bn = (v) => {
  if(Array.isArray(v))
    v = v[0]
	return new BigNumber(v.toString())
}

export const getWeb3 = (chainId) => {
  const chainConf = getChainFromId(chainId)
  const web3 = new Web3(chainConf.rpc)
  
  const usdt = new web3.eth.Contract(Erc20Abi, chainConf.usdt)
  const contract = new web3.eth.Contract(HieSwapAbi, chainConf.contract)
  const exchangeContract = new web3.eth.Contract(ExchangeAbi, chainConf.exchangeContract)
  return {web3, contract, usdt, exchangeContract}
}
export const sha3 = (v) => {
  const web3 = new Web3()
  return web3.utils.sha3(v)
}
export const tronWebApprove = async (amount, spender) => {
  const chainConf = getChainFromId(95500)
  const usdt = await window.tronWeb.contract(JSON.parse(JSON.stringify(Erc20Abi)), base58en(chainConf.usdt))
  const _spender = spender == chainConf.exchangeContract? spender: chainConf.contract
  const res = await usdt.approve(base58en(_spender), amount).send()
  return res
}
export const tronSwap = async (token, toChainId, to, amount) => {
  const chainConf = getChainFromId(95500)
  const contract = await window.tronWeb.contract(JSON.parse(JSON.stringify(HieSwapAbi)), base58en(chainConf.contract))
  const res = await contract.crossChain(base58en(token), toChainId, to , amount).send()
  return {transactionHash: "0x" + res}
}
export const tronExchange = async (token, toChainId, to, amount) => {
  const chainConf = getChainFromId(95500)
  const contract = await window.tronWeb.contract(JSON.parse(JSON.stringify(ExchangeAbi)), base58en(chainConf.exchangeContract))
  const res = await contract.crossChain(base58en(token), toChainId, to , amount).send()
  return {transactionHash: "0x" + res}
}

export const getErcBalance = async (chainId, account) => {
  if(!chainId || !account) return '0'
  const {usdt} = getWeb3(chainId)
  return await usdt.methods.balanceOf(account).call()
}
export const getAErcBalance = async (chainId, account) => {
  const chainConf = getChainFromId(chainId)
  const {web3} = getWeb3(chainId)
  const ausdt = new web3.eth.Contract(Erc20Abi, chainConf.ausdt)
  return await ausdt.methods.balanceOf(account).call()
}

export const getNaiveBalance = async (chainId, account) => {
  if(!chainId || !account) return '0'
  const {web3} = getWeb3(chainId)
  const amount = await web3.eth.getBalance(account)
  return bn(amount)
}
export const getGasPrice = async (chainId) => {
  if(!chainId) return '0'
  const {web3} = getWeb3(chainId)
  return await web3.eth.getGasPrice()
}
export const getAllowance = async (chainId, account, spender = null) => {
  if(!chainId || !account) return '0'
  const {usdt, contract} = getWeb3(chainId)
  if(spender == null){
    spender = contract._address
  }
  return await usdt.methods.allowance(account, spender).call()
}
/**
 * 注入钱包切链：await、空 provider 时回退 bitkeep/ethereum、4902 时尝试 addChain。
 * 部分 App 内 WebView（Bitget / AVE 等）不抛错但也不立刻切链，需配合 `waitForChainIdMatch` 轮询。
 */
export async function switchChain(provider, chainId) {
  if (db.get('wallet_type', 'inject') !== 'inject') return
  const p =
    provider && typeof provider.request === 'function' ? provider : getProvider()
  if (!p || typeof p.request !== 'function') {
    const err = new Error('NO_EIP1193_PROVIDER')
    err.code = 'NO_PROVIDER'
    throw err
  }
  const hex = '0x' + Number(chainId).toString(16)
  try {
    await p.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] })
  } catch (e) {
    const code = e?.code
    const msg = String(e?.message || e || '').toLowerCase()
    const needAdd =
      code === 4902 ||
      code === -32603 ||
      msg.includes('unrecognized chain') ||
      msg.includes('not added') ||
      msg.includes('not configured') ||
      msg.includes('wrong network')
    if (!needAdd) throw e
    const params = buildWalletAddEthereumChainParams(chainId)
    if (!params) throw e
    await p.request({ method: 'wallet_addEthereumChain', params: [params] })
    await p.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] })
  }
}

/** 轮询 `eth_chainId` 直到与目标一致或超时（补偿部分钱包不触发 chainChanged / 不整页刷新的情况） */
export async function waitForChainIdMatch(provider, targetChainId, opts = {}) {
  const timeoutMs = opts.timeoutMs ?? 12000
  const intervalMs = opts.intervalMs ?? 220
  const p =
    provider && typeof provider.request === 'function' ? provider : getProvider()
  if (!p?.request) return false
  const want = Number(targetChainId)
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    try {
      const raw = await p.request({ method: 'eth_chainId' })
      const cur =
        typeof raw === 'string' && raw.startsWith('0x')
          ? parseInt(raw, 16)
          : Number(raw)
      if (Number(cur) === want) return true
    } catch (_e) {
      void _e
    }
    await new Promise((r) => setTimeout(r, intervalMs))
  }
  return false
}
function isWalletConnect(){
  return db.get('wallet_type') == 'wallet_connect'
}
async function walletConnectSend(account, contract, data, provider, chainId){
  return await provider.request({
    topic: db.get('topic'),
    chainId: `eip155:${chainId}`,
    request: {
      method: 'eth_sendTransaction',
      params: [{
        from: account,
        to: contract._address,
        data,
        value: '0x00'
      }]
    }
  })
}
async function walletConnectSign(msg){
  return await provider.request({
    topic: db.get('topic'),
    chainId: `eip155:${chainId}`,
    request: {
      method: 'personal_sign',
      params: [msg]
    }
  })
}
export const approve = async (chainId, account, amount, provider, spender = null) => {
  if(!chainId || !account || !amount) throw 'error arguments'
  const {usdt, contract} = getWeb3(chainId)
  if(spender == null){
    spender = contract._address
  }
  if(chainId == 95500) return await tronWebApprove(amount, spender)
  const gasLimit = await usdt.methods.approve(spender, amount).estimateGas({from: account})
  if(isWalletConnect()){
    return await walletConnectSend(
      account, 
      usdt, 
      usdt.methods.approve(spender, amount).encodeABI(),
      provider,
      chainId,
    )
  }else{
    usdt.setProvider(provider)
    const gasPrice = await getGasPrice4Send(chainId)
    const method = usdt.methods.approve(spender, amount)
    return await sendWithGasRetry(
      method,
      {from: account, gasLimit: Math.ceil(gasLimit * 1.5), gasPrice},
      chainId
    )
  }
}

export const swap = async (chainId, toChainId, account, to, amount, provider) => {
  if (!chainId || !account || !amount) throw 'error arguments'
  const { usdt, contract } = getWeb3(chainId)
  if (chainId == 95500) return await tronSwap(usdt._address, toChainId, to, amount)
  const methodName = chainId == 56 ? 'crossChain1' : 'crossChain'
  const crossCall = contract.methods[methodName](usdt._address, toChainId, to, amount)
  const gasLimit = await crossCall.estimateGas({ from: account })
  if (isWalletConnect()) {
    return await walletConnectSend(
      account,
      contract,
      crossCall.encodeABI(),
      provider,
      chainId,
    )
  }
  contract.setProvider(provider)
  const gasPrice = await getGasPrice4Send(chainId)
  return await sendWithGasRetry(
    crossCall,
    { from: account, gasLimit: Math.ceil(gasLimit * 1.5), gasPrice },
    chainId,
  )
}

function isGasPriceTooLowError(err){
  const m = String(err?.message ?? err ?? '').toLowerCase()
  return (
    m.includes('underpriced') ||
    m.includes('fee too low') ||
    m.includes('replacement transaction underpriced') ||
    m.includes('transaction gas price is too low') ||
    m.includes('max fee per gas less than block base fee')
  )
}

function getRetryGasPrice(chainId, currentGasPriceWei){
  const chainConf = getChainFromId(chainId)
  let next = bn(currentGasPriceWei).times(1.35)
  const retryFloorGwei = Number(chainConf.gasPriceRetryFloor ?? (chainId == 56 ? 0.30 : 0))
  if(Number.isFinite(retryFloorGwei) && retryFloorGwei > 0){
    const floorWei = bn(retryFloorGwei).times(1e9)
    if(next.lt(floorWei)) next = floorWei
  }
  return next.integerValue(BigNumber.ROUND_CEIL).toFixed(0)
}

async function sendWithGasRetry(method, txOpts, chainId){
  try{
    return await method.send(txOpts)
  }catch(e){
    if(!isGasPriceTooLowError(e)) throw e
    const retryGasPrice = getRetryGasPrice(chainId, txOpts.gasPrice)
    if(bn(retryGasPrice).lte(bn(txOpts.gasPrice))) throw e
    return await method.send({
      ...txOpts,
      gasPrice: retryGasPrice,
    })
  }
}


export const exchange = async (chainId, toChainId, account, to, amount, provider) => {
  if(!chainId || !account || !amount) throw 'error arguments'
  const {usdt, exchangeContract} = getWeb3(chainId)
  if(chainId == 95500) return await tronExchange(usdt._address, toChainId, to, amount)
  const gasLimit = await exchangeContract.methods.crossChain(usdt._address, toChainId, to, amount).estimateGas({from: account})
  if(isWalletConnect()){
    return await walletConnectSend(
      account, 
      exchangeContract, 
      exchangeContract.methods.crossChain(usdt._address, toChainId, to, amount).encodeABI(),
      provider,
      chainId,
    )
  }else{
    exchangeContract.setProvider(provider)
    const gasPrice = await getGasPrice4Send(chainId)
    const method = exchangeContract.methods.crossChain(usdt._address, toChainId, to, amount)
    return await sendWithGasRetry(
      method,
      {from: account, gasLimit: Math.ceil(gasLimit * 1.5), gasPrice},
      chainId
    )
  }
}

async function getGasPrice4Send(chainId){
  const chainConf = getChainFromId(chainId)
  const applySendGasPolicy = (rawGasWei) => {
    let gasWei = bn(rawGasWei)
    const bump = Number(chainConf.gasPriceBump ?? 1.08)
    if(Number.isFinite(bump) && bump > 1){
      gasWei = gasWei.times(bump)
    }
    const floorGwei = Number(chainConf.gasPriceFloor ?? 0)
    if(Number.isFinite(floorGwei) && floorGwei > 0){
      const floorWei = bn(floorGwei).times(1e9)
      if(gasWei.lt(floorWei)){
        gasWei = floorWei
      }
    }
    return gasWei.integerValue(BigNumber.ROUND_CEIL).toFixed(0)
  }
  // User-side tx cost should follow live network conditions first.
  try{
    const liveGasPrice = await getGasPrice(chainId)
    if(liveGasPrice && Number(liveGasPrice) > 0){
      return applySendGasPolicy(liveGasPrice)
    }
  }catch(_e){
    void _e
  }
  // Fallback: use configured static gas price only when live RPC read fails.
  if(chainConf.gasPrice && chainConf.gasPrice !== 'floating'){
    return applySendGasPolicy(Number(chainConf.gasPrice) * 1e9)
  }
  return applySendGasPolicy(await getGasPrice(chainId))
}

export const getGasFee = (chainConf, gasPrice) => {
  if(chainConf.gasFee != 'floating') return chainConf.gasFee
  const gWei = bn(gasPrice != null && gasPrice !== '' ? gasPrice : 0)
  if (!gWei.isFinite() || gWei.lte(0)) return 'loading'
  return gWei
    .times(130000)
    .div(bn(10).pow(18))
    .times(bn(chainConf.nativePrice))
    .times(bn(1.1))
    .toFixed(2)
}

export const signMessage = async (provider, msg, account) => {
  if(isWalletConnect()){
    return await walletConnectSign(msg)
  }else{
    return await provider.request({method: 'personal_sign', params: [msg, account]})
  }
}

export const withdraw = async (provider, account, amount, deadline, r, s, v) => {
  const web3 = new Web3(getChainFromId(201022).rpc)
  const abi = [{ "inputs": [ { "internalType": "uint256", "name": "amount", "type": "uint256" }, { "internalType": "uint256", "name": "deadline", "type": "uint256" }, { "internalType": "bytes32", "name": "r", "type": "bytes32" }, { "internalType": "bytes32", "name": "s", "type": "bytes32" }, { "internalType": "uint8", "name": "v", "type": "uint8" } ], "name": "withdraw", "outputs": [], "stateMutability": "nonpayable", "type": "function" }]
  const contract = new web3.eth.Contract(abi, WITHDRAW)
  if(isWalletConnect()){
    return await walletConnectSend(
      account, 
      contract, 
      contract.methods.withdraw(amount, deadline, r.toLowerCase(), s.toLowerCase(), v.toLowerCase()).encodeABI(),
      provider,
      201022,
    )
  }else{
    contract.setProvider(provider)
    const gasPrice = await getGasPrice4Send(201022)
    return await contract.methods.withdraw(amount, deadline, r.toLowerCase(), s.toLowerCase(), v.toLowerCase()).send({from: account, gasLimit: 1000000, gasPrice})
  }
}